import { Injectable } from "@angular/core";
import Bugsnag from "@bugsnag/js";
import { lastValueFrom } from "rxjs";
import { BaseAppInitService } from "@apps/patient-core/src/app/services/abstracts/app-init.service";
import { notifyAndLeaveBreadcrumb, setupBugsnag } from "@apps/patient-core/src/app/utils/logging";
import { environment } from "@apps/patient-core/src/app/environments/environment";
import * as utils from "@apps/patient-core/src/app/utils";
import { BookableApptsService } from "../bookable-appts.service";
import { BaseCommonService } from "@apps/patient-core/src/app/services/abstracts/common.service";
import { BaseBrandService } from "@apps/patient-core/src/app/services/abstracts/brand.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { HttpClient } from "@angular/common/http";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { LocationService } from "@apps/patient-core/src/app/services/location.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { BaseSessionService } from "@apps/patient-core/src/app/services/abstracts/session.service";
import { getShortCodeHelpers } from "../../helpers/short-code.helpers";

const PREVIOUS_SHORT_CODE_STORAGE_KEY = "previous_short_code";

@Injectable({
  providedIn: "root",
})
export class PatientAppInitService extends BaseAppInitService {
  constructor(
    private _commonService: BaseCommonService,
    private _brandService: BaseBrandService,
    private _patientService: BasePatientsService,
    private _authService: BaseAuthService,
    private _bookableApptsService: BookableApptsService,
    private _locationService: LocationService,
    private _cacheService: CacheService,
    private _http: HttpClient,
    private _navigationService: NavigationService,
    private _sessionService: BaseSessionService
  ) {
    super();
  }
  // eslint-disable-next-line complexity
  protected async _doInit() {
    if (this._handleRootDomain()) {
      return;
    }

    if (!(await this._handleMergeTagFallbackLink())) {
      if (await this._handlePreferencesDomain()) return;

      await this._handleShortCode();

      // The domain must include a brand subdomain so we need to determine the stage. This needs to be done before starting Bugsnag
      await this._determineDomainStage();
    }

    setupBugsnag();

    this.checkIfStorageSupported();

    try {
      // Wait for domain info to be fetched before continuing so we correctly set info such as the country code/brand info avoiding any race conditions
      await this._commonService.getDomainInfo();

      if (this._authService.isPatient()) {
        await this._patientService.getPatient();
      }
    } catch (error) {
      if (error !== 302) {
        console.error("Failed to get domain info or patient", error);

        utils.hideAppLoadingSpinner();
      }

      return;
    }

    // If we're not logged in as a patient we need to check the url for it's requested alias here
    if (!this._authService.isPatient()) {
      const { pathname } = this._locationService;
      if (pathname?.startsWith("/practices/")) {
        let alias = pathname.substring(11);
        if (alias.includes("/")) alias = alias.substring(0, alias.indexOf("/"));
        const aliasOk = await this._brandService.setRestrictedSiteIdFromAlias(alias);

        // If the alias isn't ok we should just return and then rely on brand-site-alias.component.ts to redirect us to the error page
        if (!aliasOk) return;
      }
    }

    if (this._shouldGetBookableAppts()) {
      await this._bookableApptsService.getBookableAppts();
    }
  }

  private _redirectToLinkExpired(): void {
    this._navigationService.navigate("/restricted/expired");
  }

  private async _handleShortCode(): Promise<void> {
    const { shortCode } = getShortCodeHelpers(this._locationService.pathname, this._locationService.search);

    if (!shortCode) {
      return;
    }

    if (shortCode !== this._cacheService.getSession(PREVIOUS_SHORT_CODE_STORAGE_KEY) && this._authService.isPatient()) {
      // Clear the session if the short code has changed and the user is logged in as a patient to prevent the wrong details appearing
      await this._sessionService.clear();
    }

    // Set the previous short code to the current short code so we can check if it's changed next time
    this._cacheService.setSession(PREVIOUS_SHORT_CODE_STORAGE_KEY, shortCode);
  }

  private async _handlePreferencesDomain(): Promise<boolean> {
    if (!this._locationService.isPreferencesDomain) return false;

    const path = "preferences/unsubscribe";
    const { pathname } = this._locationService;

    setupBugsnag();

    if (pathname === `/${path}`) return true;

    // Clear any existing cache data (JWT etc) just in case the patient has recently used the patient app
    this._cacheService.clearAll();

    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());

    await this._determinePracticeStage(queryParams.practice_id);

    this._navigationService.navigate("preferences/unsubscribe", {
      state: queryParams,
      replaceUrl: true,
    });

    return true;
  }

  private _shouldGetBookableAppts(): boolean {
    if (this._authService.isPatient()) {
      return !this._patientService?.patientInfo?.prevent_appointment_booking;
    }

    // If we're not going to support new patients at this brand and a patient isn't logged in yet, there's no point in checking bookable appointments
    const existingOnly = this._brandService.brand.existing_patients_only_on_brand_url && !this._brandService.restrictedSite;
    return !existingOnly;
  }

  private _handleRootDomain(): boolean {
    if (!this._locationService.isRootDomain) {
      return false;
    }

    const shortCodeHelpers = getShortCodeHelpers(this._locationService.pathname, this._locationService.search);

    if (shortCodeHelpers.isShortCode || shortCodeHelpers.isMergeTagFallback) {
      // Link must have expired, so redirect to the expired page
      this._redirectToLinkExpired();
    }

    return true;
  }

  private async _handleMergeTagFallbackLink(): Promise<boolean> {
    const shortCodeHelpers = getShortCodeHelpers(this._locationService.pathname, this._locationService.search);

    if (!shortCodeHelpers.isMergeTagFallback) {
      return false;
    }

    const { practiceId, patientId } = shortCodeHelpers.mergeTagFallback!;

    const response = (await lastValueFrom(
      this._http.get(`${environment.GLOBAL_URL}/v1/patients/${patientId}`, {
        headers: {
          practice: practiceId,
        },
      })
    )) as { token: string };

    this._authService.setToken(response.token);

    return true;
  }

  private _determineDomainStage(): Promise<void> {
    if (this._locationService.isRootDomain) {
      // No point looking up the stage for the root domain. This will be handled by the short code
      return Promise.resolve();
    }

    return this._determineStage(`domain=${this._locationService.actualHostname}`);
  }

  private _determinePracticeStage(practiceId: string): Promise<void> {
    return this._determineStage(`practice_id=${practiceId}`);
  }

  private async _determineStage(queryStringParameters: string): Promise<void> {
    try {
      const { GLOBAL_URL } = environment;
      const response = (await lastValueFrom(this._http.get(`${GLOBAL_URL}/v1/stages?${queryStringParameters}`))) as { stage: string };
      const { stage } = response;

      if (window.STAGE !== stage) {
        Bugsnag.notify(`Domain stage changed from ${window.STAGE} to ${stage} for ${queryStringParameters}`);
      }

      window.STAGE = stage;
    } catch {
      Bugsnag.notify(new Error(`Failed to determine domain stage for ${queryStringParameters}`));
      // Let the downstream logic handle it and go to the error page
    }
  }

  private _handleCookies(cookieString: string): void {
    if (!cookieString) {
      return;
    }

    const cookies = cookieString
      .split(";")
      .map((cookie) => cookie.trim())
      .reduce((acc, cookie) => {
        const [key, value] = cookie.split("=");

        acc[key] = value;

        return acc;
      }, {} as Record<string, string>);

    if (!cookies.region || !cookies.stage) {
      return;
    }

    window.STAGE = cookies.stage;
    window.REGION = cookies.region;
  }

  public async init() {
    this._handleCookies(document.cookie);

    // Initialize the session service first to ensure we have the session cookie set
    await this._sessionService.init();

    try {
      await this._doInit();
    } catch (error) {
      notifyAndLeaveBreadcrumb("Failed to initialize app", {
        error,
        jwt: this._authService.getJWT(),
      });

      this._navigationService.navigate("/error", {
        state: {
          error: "Failed to initialise the patient application",
        },
      });
    }
  }
}
